/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import moment from 'moment';
import React, { useContext, useEffect, useMemo } from 'react';
import { Card, List } from 'semantic-ui-react';

import { AppStateContext } from 'api/AppStateContext';
import * as dto from 'client/dto';
import { ProximalCaseVM } from 'models/EventModels';
import { RiskDirectionType } from 'models/RiskCategories';
import { DisableTRANSPAR } from 'utils/constants';
import { sxtheme } from 'utils/cssHelpers';
import { formatRelativeDate, parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';
import { isMobile, isNonMobile } from 'utils/responsive';

import { ListLabelsHeader, SectionHeader } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';
import BdButton from 'components/_controls/BdButton/BdButton';
import { BdTooltip } from 'components/_controls/BdTooltip';
import {
  DiseaseActivityCard,
  GetDiseaseActivityApiFunction,
} from 'components/_controls/DiseaseActivityCard';
import { ProximalCaseCard } from 'components/_controls/ProximalCaseCard';
import { UnderstandingCaseAndDeathReporting } from 'components/_static/UnderstandingCaseAndDeathReporting';
import { Accordian } from 'components/Accordian';
import { Error } from 'components/Error';
import DashboardToolbar from 'components/Navigationbar/DashboardToolbar';
import { ILoadableProps, IPanelProps, Panel } from 'components/Panel';
import {
  RiskOfExportation,
  RiskOfImportation,
  RisksProjectionCard,
} from 'components/RisksProjectionCard';
import { GetSelectedRisk } from 'components/RisksProjectionCard/RisksProjectionCard';
import { ActivePanel } from 'components/SidebarView/sidebar-types';
import {
  PopupAirportExport,
  PopupAirportImport,
  PopupTotalExport,
  PopupTotalImport,
} from 'components/TransparencyTooltips';

import GoogleTranslateLogoSvg from 'assets/google-translate-logo.svg';

import { AirportExportationItem, AirportImportationItem } from './AirportItem';
import OutbreakSurveillanceOverall from './OutbreakSurveillanceOverall';
import ReferenceList from './ReferenceList';

type EventDetailPanelProps = IPanelProps &
  ILoadableProps & {
    activePanel: ActivePanel;
    event: dto.GetEventModel;
    hasError: boolean;
    onZoomToLocation: () => void;
    handleRetryOnClick: () => void;
    onRiskParametersClicked: () => void;
    onGoToActivityHistory: (activityDate?: string) => void;
    isRiskParametersSelected: boolean;
    selectedRiskType: RiskDirectionType;
    onSelectedRiskTypeChanged: (val: RiskDirectionType) => void;
    onNeedApiGetDiseaseActivity: GetDiseaseActivityApiFunction;
  };

const EventDetailPanelDisplay: React.FC<EventDetailPanelProps> = ({
  isLoading,
  activePanel,
  event,
  hasError,
  onClose,
  isMinimized,
  onMinimize,
  onZoomToLocation,
  handleRetryOnClick,
  onRiskParametersClicked,
  onGoToActivityHistory,
  isRiskParametersSelected,
  selectedRiskType,
  onSelectedRiskTypeChanged,
  onNeedApiGetDiseaseActivity,
}) => {
  const isNonMobileDevice = isNonMobile(useBreakpointIndex());
  const isMobileDevice = isMobile(useBreakpointIndex());
  const {
    isLocal,
    caseCounts,
    importationRisk,
    exportationRisk,
    eventInformation: { title, lastUpdatedDate, startDate: eventStartDate },
    eventLocations,
    airports,
    airports: { sourceAirports, destinationAirports },
    diseaseInformation,
    outbreakPotentialCategory,
    articles,
    calculationMetadata,
  } = event;

  const selectedRisk = GetSelectedRisk(event, selectedRiskType);
  const { appState, amendState } = useContext(AppStateContext);
  const { proximalData } = appState;
  const timeframeStartDate = useMemo(
    () => moment(appState.startDate).toDate(),
    [appState.startDate]
  );
  const filteredArticles = useMemo(
    () =>
      articles.filter(
        article => parseIsoDateStartOfDayUtc0(article.publishedDate) >= timeframeStartDate
      ),
    [articles]
  );

  const handleProximalDetailsExpanded = (isExpanded: boolean) => {
    amendState({
      isProximalDetailsExpandedEDP: isExpanded,
    });
  };

  useEffect(() => {
    amendState({ isProximalDetailsExpandedEDP: false });
  }, [amendState]);

  const proximalVM: ProximalCaseVM = useMemo(
    () => proximalData && diseaseInformation && proximalData[diseaseInformation.id],
    [proximalData, diseaseInformation]
  );

  return (
    <Panel
      isAnimated
      title={title || appState.eventTitleFallback || 'Loading...'}
      isLoading={isLoading}
      onClose={onClose}
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      subtitleMobile={
        !appState.locationFullName ? undefined : appState.locationFullName || 'Loading...'
      }
      toolbar={isMobileDevice && <DashboardToolbar showToolbar />}
    >
      {hasError ? (
        <Error
          title="Something went wrong."
          subtitle="Please check your network connectivity and try again."
          linkText="Click here to retry"
          linkCallback={handleRetryOnClick}
        />
      ) : (
        <>
          <div
            sx={{
              p: '16px',
              bg: sxtheme(t => t.colors.deepSea10),
            }}
          >
            {isNonMobileDevice && eventLocations?.length > 0 && (
              <div sx={{ mb: '8px' }}>
                <BdButton
                  type="tertiary"
                  onClick={onZoomToLocation}
                  prefixIcon="icon-target"
                  text="Zoom to Location"
                  small
                />
              </div>
            )}

            <Typography variant="caption" color="stone50">
              Last checked on {formatRelativeDate(lastUpdatedDate, 'AbsoluteDate')}
            </Typography>

            <DiseaseActivityCard
              isSelected={activePanel === 'HistoryPanel'}
              countryName={
                (event.eventLocations && event.eventLocations[0]?.countryName) || 'this country'
              }
              firstActivityUpdateDateStr={event?.eventInformation?.firstActivityUpdateDate}
              caseCounts={event?.caseCounts}
              onNeedApiGetDiseaseActivity={onNeedApiGetDiseaseActivity}
              onGoToActivityHistory={onGoToActivityHistory}
            />

            {proximalVM && isLocal && (
              <ProximalCaseCard
                vm={proximalVM}
                onCardOpenedChanged={handleProximalDetailsExpanded}
              />
            )}

            <RisksProjectionCard
              importationRisk={importationRisk}
              exportationRisk={exportationRisk}
              outbreakPotentialCategory={outbreakPotentialCategory}
              diseaseInformation={diseaseInformation}
              onClick={
                !selectedRisk?.isModelNotRun && !selectedRisk?.isModelRunning
                  ? onRiskParametersClicked
                  : null
              }
              isSelected={isRiskParametersSelected}
              riskType={selectedRiskType}
              onRiskTypeChanged={onSelectedRiskTypeChanged}
            />
          </div>
          <Accordian expanded={false} title="Case Surveillance" sticky>
            <Accordian
              expanded={false}
              title="Understanding Case/Death Reporting"
              rounded
              sx={{ mb: '24px' }}
            >
              <UnderstandingCaseAndDeathReporting />
            </Accordian>
            <OutbreakSurveillanceOverall caseCounts={caseCounts} eventLocations={eventLocations} />
          </Accordian>

          <Accordian expanded={false} title="Risk of Importation" sticky>
            {!!importationRisk && (
              <>
                <SectionHeader icon="icon-plane-import">Overall</SectionHeader>
                <BdTooltip
                  className="transparency"
                  customPopup={
                    <PopupTotalImport
                      airports={airports}
                      eventStartDate={eventStartDate}
                      appMetadata={appState.appMetadata}
                      locationFullName={appState.locationFullName}
                    />
                  }
                  wide="very"
                  disabled={
                    DisableTRANSPAR ||
                    importationRisk.isModelNotRun ||
                    importationRisk.isModelRunning
                  }
                >
                  <Card fluid className="borderless">
                    <RiskOfImportation
                      risk={importationRisk}
                      showCovidDisclaimerTooltip="if calculated"
                    />
                  </Card>
                </BdTooltip>
              </>
            )}

            <SectionHeader>Airports Globally with &gt;1% Risk of Importation</SectionHeader>
            <ListLabelsHeader
              lhs={['Destination airport']}
              rhs={['Likelihood of case importation', 'Estimated case importations']}
            />
            <List className="xunpadded">
              {destinationAirports?.map(x => (
                <List.Item key={x.id}>
                  <BdTooltip
                    className="transparency individual"
                    customPopup={
                      <PopupAirportImport
                        airport={x}
                        totalDestinationVolume={airports?.totalDestinationVolume}
                        totalSourceVolume={airports?.totalSourceVolume}
                        eventStartDate={eventStartDate}
                        appMetadata={appState.appMetadata}
                      />
                    }
                    wide="very"
                    disabled={DisableTRANSPAR}
                  >
                    <AirportImportationItem airport={x} />
                  </BdTooltip>
                </List.Item>
              )) || (
                <Typography
                  variant="body2"
                  color="stone90"
                  sx={{ textAlign: 'center', fontStyle: 'italic' }}
                >
                  {!importationRisk ||
                  importationRisk.isModelNotRun ||
                  exportationRisk.isModelRunning
                    ? 'No airports returned because risk was not calculated'
                    : 'No airports with >1% risk of importation'}
                </Typography>
              )}
            </List>
          </Accordian>

          {!!exportationRisk && (
            <Accordian expanded={false} title="Risk of Exportation" sticky>
              <SectionHeader icon="icon-plane-export">Overall</SectionHeader>
              <BdTooltip
                className="transparency"
                customPopup={
                  <PopupTotalExport
                    airports={airports}
                    eventStartDate={eventStartDate}
                    calculationMetadata={calculationMetadata}
                    caseCounts={caseCounts}
                    eventTitle={title || appState.eventTitleFallback || 'Loading...'}
                    appMetadata={appState.appMetadata}
                  />
                }
                wide="very"
                disabled={
                  DisableTRANSPAR ||
                  exportationRisk.isModelNotRun ||
                  exportationRisk.isModelRunning ||
                  !calculationMetadata
                }
              >
                <Card fluid className="borderless">
                  <RiskOfExportation
                    risk={exportationRisk}
                    showCovidDisclaimerTooltip="if calculated"
                  />
                </Card>
              </BdTooltip>
              <SectionHeader>
                Airports with &gt;1% likelihood of use from activity location(s)
              </SectionHeader>
              <ListLabelsHeader
                lhs={['Source airport']}
                rhs={['Likelihood of case exportation', 'Estimated case exportations']}
              />
              <List className="xunpadded">
                {sourceAirports?.map(x => (
                  <List.Item key={x.id}>
                    <BdTooltip
                      className="transparency individual"
                      customPopup={
                        <PopupAirportExport
                          airport={x}
                          totalDestinationVolume={airports?.totalDestinationVolume}
                          totalSourceVolume={airports?.totalSourceVolume}
                          eventStartDate={eventStartDate}
                          appMetadata={appState.appMetadata}
                        />
                      }
                      wide="very"
                      disabled={DisableTRANSPAR}
                    >
                      <AirportExportationItem airport={x} />
                    </BdTooltip>
                  </List.Item>
                )) || (
                  <Typography
                    variant="body2"
                    color="stone90"
                    sx={{ textAlign: 'center', fontStyle: 'italic' }}
                  >
                    {!exportationRisk ||
                    exportationRisk.isModelNotRun ||
                    exportationRisk.isModelRunning
                      ? 'No airports returned because risk was not calculated'
                      : 'No airports with >1% likelihood of use from activity location(s)'}
                  </Typography>
                )}
              </List>
            </Accordian>
          )}
          {!!filteredArticles.length && (
            <Accordian expanded={false} title="References" yunpadContent sticky>
              <ReferenceList articles={filteredArticles} timeframeStartDate={timeframeStartDate} />
              {!!articles.some(a => a.originalLanguage !== 'en') && (
                <a href="https://translate.google.com/" target="_blank" rel="noopener noreferrer">
                  <img
                    src={GoogleTranslateLogoSvg}
                    alt="translated by Google"
                    sx={{ marginTop: 16, marginBottom: 16 }}
                  />
                </a>
              )}
            </Accordian>
          )}
        </>
      )}
    </Panel>
  );
};

export default EventDetailPanelDisplay;
